import {
	Component,
	ComponentInterface,
	h,
	Method,
	Prop,
	Element,
} from '@stencil/core';
import {
	generateVideoUrl,
	fetchMetaData,
	getTextTracks,
	validateYoutubeVideoId,
	areMarketingCookiesAllowed,
} from '../../_helpers/video-player';
import {
	PatchedVideoJsPlayerOptions,
	videoPlayerConfig,
} from './video-player.config';
import { VideoEnvironment } from '../../../../common/_types/environment';
import {
	VideoMetaRequestMode,
	VideoMetaResponse,
} from './video-player.interface';
import { PlayerType } from '../../../../../_storybook-helpers/video';

import {
	getCwpEnvironment,
	getVideoEnvFromCwpEnv,
} from '../../../../common/_helpers/environment';
import { dsConsole } from '../../_helpers/console';
import type { VideoJsPlayer } from 'video.js';
import { VideoJsPlayerWithYouTubeOptions } from '../../../../common/_types/video-player';

const jsonMessage = 'Video Player is in JSON mode';
const jsonpMessage = `To avoid localhost CORS issues, the Video Player is in JSONP mode for fetching the video metadata.
This is different to production where JSON is used (with Axios). 
To emulate production, run the proxy server with 'npm run proxy:cloudfront' and then change the Meta Request Mode field to 'JSON'.
NOTE: Using JSONP does not prevent CORS blocking of Audio Descriptions or Captions files in Storybook. For this you must be in local JSON mode or in a CWP environment.`;

const delayForPlaylistInsideBodyTabsInit = 500; // Playlists don't initialise properly inside Body Tabs without this setTimeout workaround

@Component({
	tag: 'ds-video-player',
	shadow: false,
})
export class VideoPlayer implements ComponentInterface {
	private videojs;
	private videoJsPlayer: VideoJsPlayer;
	videoEl: HTMLVideoElement;

	@Element() el: HTMLDsVideoPlayerElement;

	@Prop() environment: VideoEnvironment;
	@Prop() metaRequestMode: VideoMetaRequestMode = 'json';
	@Prop() playerType: PlayerType = 'blackrock';
	@Prop() includePlaylist = false;
	@Prop() videoId: string;
	@Prop() poster: string;

	isInitialised: Promise<void>;
	initSuccess;
	initFail;

	@Method() onInit(): Promise<void> {
		return this.isInitialised;
	}

	connectedCallback() {
		if (!this.isInitialised) {
			this.isInitialised = new Promise((resolve, reject) => {
				this.initSuccess = resolve;
				this.initFail = reject;
			});
		}
	}

	async initVideo() {
		window.VIDEOJS_NO_DYNAMIC_STYLE = true;
		const videojs = await import('video.js');
		this.videojs = videojs.default;
		const { HotkeysPlugin } = await import('./videojs-hotkeys-plugin');
		if (!window.DS?.videoPlayer?.hotkeysPluginLoaded) {
			this.videojs.registerPlugin('hotkeys', HotkeysPlugin);
		}
		window.DS = window.DS || {};
		window.DS.videoPlayer = window.DS.videoPlayer || {};
		window.DS.videoPlayer.hotkeysPluginLoaded = true;
		await import('videojs-seek-buttons');

		if (this.playerType === 'youtube') {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			await import('videojs-youtube');
		}

		if (this.includePlaylist) {
			setTimeout(() => {
				this.initVideoWithPlaylist();
			}, delayForPlaylistInsideBodyTabsInit);
		} else {
			await this.initSingleVideo();
		}
	}

	componentDidLoad() {
		// Override for local CWP development to get around CORS issues
		if (window.location.host === 'localhost:1776') {
			this.metaRequestMode = 'jsonp';
		}

		if (this.metaRequestMode === 'jsonp') {
			dsConsole.warn(jsonpMessage);
		}
		if (this.metaRequestMode === 'json') {
			dsConsole.warn(jsonMessage);
		}

		if (!this.environment) {
			this.environment = getVideoEnvFromCwpEnv(getCwpEnvironment());
		}

		dsConsole.warn(
			'Video Player initialisation starting. Environment set to',
			this.environment,
			this.videoId
		);

		this.initVideo();
	}

	/**
	 * Returns a promise that returns the element where videoJs is instantiated.
	 */
	@Method() async getVideoEl(): Promise<HTMLVideoElement> {
		return this.isInitialised.then(() => this.videoEl);
	}

	/**
	 * Returns a promise that returns the videoJs instance.
	 */
	@Method() async getVideoJsInstance(): Promise<VideoJsPlayer> {
		return this.isInitialised.then(() => this.videoJsPlayer);
	}

	async initVideoWithPlaylist() {
		try {
			// Initialise video player with no videos (the playlist component will do the rest)
			this.videoJsPlayer = this.videojs(this.videoEl, {
				...videoPlayerConfig,
				youtube: {
					customVars: { wmode: 'transparent', modestbranding: 1 },
					enablePrivacyEnhancedMode: !areMarketingCookiesAllowed(),
				},
			});
			this.initSuccess();
		} catch (error) {
			dsConsole.error(error);
			this.initFail(error);
		}
	}

	async initSingleVideo() {
		try {
			if (this.playerType === 'youtube') {
				validateYoutubeVideoId(this.videoId);
				const youtubeVideoPlayerConfig: VideoJsPlayerWithYouTubeOptions = {
					sources: [
						{
							type: 'video/youtube',
							src: 'https://www.youtube.com/watch?v=' + this.videoId,
						},
					],
					techOrder: ['youtube'],
					youtube: {
						customVars: { wmode: 'transparent', modestbranding: 1 },
						enablePrivacyEnhancedMode: !areMarketingCookiesAllowed(),
					},
					...videoPlayerConfig,
				};

				this.videoJsPlayer = this.videojs(
					this.videoEl,
					youtubeVideoPlayerConfig
				);
			} else {
				const videoMeta: VideoMetaResponse = await fetchMetaData(
					this.videoId,
					this.environment,
					this.metaRequestMode
				);
				// Setup video JS options
				const standardVideoPlayerConfig: PatchedVideoJsPlayerOptions = {
					sources: [
						{
							type: 'video/mp4',
							src: generateVideoUrl(videoMeta),
						},
					],
					poster: this.poster || videoMeta.thumburl + videoMeta.thumbs[1][1],
					...videoPlayerConfig,
				};

				this.videoJsPlayer = this.videojs(
					this.videoEl,
					standardVideoPlayerConfig
				);

				const tracks = getTextTracks(videoMeta, this.environment);

				this.videoJsPlayer.ready(function () {
					tracks.forEach((track) => this.addRemoteTextTrack(track, false));
				});
			}

			this.initSuccess();
		} catch (error) {
			dsConsole.error(error);
			this.initFail(error);
		}
	}

	disconnectedCallback() {
		if (this.videoJsPlayer) {
			this.videoJsPlayer.dispose();
		}
	}

	render() {
		return (
			<video
				data-chromatic="ignore"
				id={'v' + this.videoId}
				ref={(el) => (this.videoEl = el as HTMLVideoElement)}
				class="video-js"
				controls
				preload="none"
			></video>
		);
	}

	[memberName: string]: any;
}
